/**
 * The arithmetic behind cropping a photograph to a square.
 *
 * Separated from the component on purpose. Everything here is a pure function of numbers,
 * and it is the part that is easy to get subtly wrong — an off-by-one in the source
 * rectangle is a crop that drifts a few pixels every time somebody zooms, which nobody
 * notices until their face is off centre. jsdom has no layout, so a component test cannot
 * check any of it; these can be checked exactly.
 *
 * **The model.** A square viewport of `side` CSS pixels. The photograph is drawn at `scale`
 * and offset by `(x, y)` in viewport coordinates, where the offsets are zero or negative —
 * the image is always at least as large as the hole it is seen through, so its top-left
 * corner sits at or above and to the left of the viewport's.
 */

export interface Frame {
  /** Natural pixel size of the photograph. */
  width: number
  height: number
}

export interface Placed {
  scale: number
  x: number
  y: number
}

/**
 * The smallest scale at which the photograph still covers the viewport.
 *
 * `max`, not `min`: at `min` the shorter side would fit inside the square and leave a gap,
 * and a gap in an avatar is a grey bar somebody did not choose.
 */
export function coverScale(frame: Frame, side: number): number {
  if (!frame.width || !frame.height) return 1
  return Math.max(side / frame.width, side / frame.height)
}

/** How far in somebody may go. Past this a phone photograph is visibly soft. */
export const MAX_ZOOM = 4

/**
 * `place`, with the offsets pulled back inside their limits.
 *
 * Clamping here rather than at the point of dragging means every route into a new position
 * — a drag, the zoom slider, an arrow key — lands somewhere legal by construction, instead
 * of each one having to remember.
 */
export function contain(frame: Frame, side: number, place: Placed): Placed {
  const floor = coverScale(frame, side)
  const scale = Math.min(Math.max(place.scale, floor), floor * MAX_ZOOM)
  const width = frame.width * scale
  const height = frame.height * scale
  return {
    scale,
    // `side - width` is negative or zero, and is the furthest left the image may be pushed
    // before its right edge would come into view.
    x: Math.min(0, Math.max(side - width, place.x)),
    y: Math.min(0, Math.max(side - height, place.y)),
  }
}

/** Where a photograph starts: covering the viewport, centred. */
export function centred(frame: Frame, side: number): Placed {
  const scale = coverScale(frame, side)
  return contain(frame, side, {
    scale,
    x: (side - frame.width * scale) / 2,
    y: (side - frame.height * scale) / 2,
  })
}

/**
 * Zoom about the middle of the viewport rather than about the top-left corner.
 *
 * Scaling without this walks the picture out from under the frame: the corner stays put, so
 * everything the reader was looking at slides away as they zoom, and they have to drag it
 * back every time. Keeping the centre fixed is what makes a zoom slider feel like a zoom.
 */
export function zoomTo(frame: Frame, side: number, place: Placed, scale: number): Placed {
  const middle = side / 2
  // Where the viewport's centre falls on the photograph, in natural pixels.
  const onImageX = (middle - place.x) / place.scale
  const onImageY = (middle - place.y) / place.scale
  return contain(frame, side, {
    scale,
    x: middle - onImageX * scale,
    y: middle - onImageY * scale,
  })
}

/**
 * The rectangle of the original to copy out, in natural pixels.
 *
 * The viewport shows `side / scale` natural pixels across, starting `-x / scale` in from the
 * left. Everything is divided by the same scale, so the answer does not depend on how big
 * the preview happened to be on screen — which matters, because the preview is sized by CSS
 * and the exported square never is.
 */
export function sourceRect(place: Placed, side: number): {
  sx: number
  sy: number
  sw: number
  sh: number
} {
  return {
    sx: -place.x / place.scale,
    sy: -place.y / place.scale,
    sw: side / place.scale,
    sh: side / place.scale,
  }
}
